import {
    Box,
    Card,
    CardMedia,
    IconButton,
    Typography,
} from '../../../imports/muiImport';
import {
    noImageAvailble,
    useDispatch,
    useSelector,
    useState
} from "../../../imports/generalImport";
import { Delete, CheckCircle, Edit } from '@mui/icons-material';
import { Swiper, SwiperSlide } from 'swiper/react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import 'swiper/css';
import 'swiper/css/grid';
import 'swiper/css/navigation';

import { Grid as SwiperGrid, Navigation, Grid } from 'swiper/modules';
import { removePost, setSelectedPost } from "../../../store/giftcardDesign/action";
import { Chip, CircularProgress } from '@mui/material';
import { useRef } from 'react';


const GreetCover = () => {
    const [loading, setLoading] = useState(false);
    const { posts: giftCardTemplates, selectedPost } = useSelector((state) => state.GiftCardDesign);
    const dispatch = useDispatch();
    const swiperRef = useRef(null);

    const handleCardSelect = (post) => {
        dispatch(setSelectedPost(post));
    };

    const handleDelete = (postId, e) => {
        e.stopPropagation();
        const postToDelete = giftCardTemplates.find(post => post.id === postId);

        // Prevent deletion if not deletable
        if (!postToDelete?.isDeleteable) {
            console.warn("Cannot delete default posts");
            return;
        }

        setLoading(true);
        try {
            dispatch(removePost(postId));

            // If we're deleting the currently selected post
            if (selectedPost?.id === postId) {
                const remainingPosts = giftCardTemplates.filter(post => post.id !== postId);

                // Priority 1: Select last remaining image if exists
                if (remainingPosts.length > 0) {
                    dispatch(setSelectedPost(remainingPosts[remainingPosts.length - 1]));
                }
                // Priority 2: Fallback to default image (isDefault: true)
                else {
                    const defaultImage = giftCardTemplates.find(post => post.isDefault);
                    if (defaultImage) {
                        dispatch(setSelectedPost(defaultImage));
                    }
                    // Priority 3: As last resort, select first image if exists
                    else if (giftCardTemplates.length > 0) {
                        dispatch(setSelectedPost(giftCardTemplates[0]));
                    }
                }
            }
        } catch (error) {
            console.error("Error deleting post:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (postId, e) => {
        e.stopPropagation();
        console.log("Editing post:", postId);
        // Implement your edit logic here
    };

    const GreetCard = ({ post, isSelected }) => {
        return (
            <Card
                onClick={() => handleCardSelect(post)}
                sx={{
                    borderRadius: 3,
                    boxShadow: isSelected ? 5 : 0,
                    position: "relative",
                    cursor: "pointer",
                    width: '100%', // Take full width of SwiperSlide
                    height: 'auto', // Increased height for better 3x2 grid
                    maxHeight: 130,
                    '& .action-buttons': {
                        opacity: 1
                    },
                    '&:hover': {
                        boxShadow: 3,
                    },
                    ...(post.isDefault && {
                        '&:after': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'linear-gradient(to top, rgba(0,0,0,0.3) 0%, transparent 50%)',
                        }
                    })
                }}
            >
                {/* Action buttons */}
                <Box className="action-buttons" sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    opacity: isSelected ? 1 : 0,
                    transition: 'opacity 0.2s ease',
                    zIndex: 2,
                    '&:hover': {
                        opacity: 1
                    }
                }}>
                    {isSelected && (
                        <IconButton
                            size="small"
                            sx={{
                                backgroundColor: 'rgba(255,255,255,1)',
                                '&:hover': { backgroundColor: 'rgba(255,255,255,1)' },
                            }}
                        >
                            <CheckCircle color="secondary" fontSize="small" />
                        </IconButton>
                    )}

                    {/* Only show delete button for deletable posts */}
                    {post.isDeleteable && (
                        <IconButton
                            size="small"
                            sx={{
                                backgroundColor: 'rgba(255,255,255,0.9)',
                                '&:hover': { backgroundColor: 'rgba(255,255,255,1)' }
                            }}
                            onClick={(e) => handleDelete(post.id, e)}
                            disabled={loading}
                        >
                            {loading ? <CircularProgress size={20} /> : <Delete color="error" fontSize="small" />}
                        </IconButton>
                    )}

                    {/* Show edit button for custom posts */}
                    {post?.isCustom && (
                        <IconButton
                            size="small"
                            sx={{
                                backgroundColor: 'rgba(255,255,255,0.9)',
                                '&:hover': { backgroundColor: 'rgba(255,255,255,1)' }
                            }}
                            onClick={(e) => handleEdit(post.id, e)}
                        >
                            <Edit color="secondary" fontSize="small" />
                        </IconButton>
                    )}
                </Box>

                {post?.isDefault && (
                    <Chip
                        label={'Default'}
                        sx={{
                            position: 'absolute',
                            top: 4,
                            left: 3,
                            height: 15,
                            textAlign: 'center',
                            color: 'dark.main',
                            textTransform: "lowercase",
                            backgroundColor: 'rgba(255,255,255,1)',
                            ".MuiChip-label": {
                                px: 0.5,
                            }
                        }}
                    />
                )}

                <CardMedia
                    component="img"
                    image={post?.image || noImageAvailble}
                    alt={post?.altText || `Gift card design ${post?.id}`}
                    sx={{
                        width: "100%",
                        height: "auto",
                        maxHeight: 130,
                        objectFit: post?.isDeleteable === true ? 'cover' : 'initial'
                    }}
                    onError={(e) => {
                        e.target.src = { noImageAvailble };
                    }}
                />
            </Card>
        );
    };

    const CardContainer = () => {
        return (
            <Box sx={{ position: 'relative', width: '100%', px: 2 }}>
                <Swiper
                    slidesPerView={'auto'}
                    grid={{
                        rows: giftCardTemplates?.length > 3 ? 2 : 1,
                        fill: 'row'
                    }}
                    onSwiper={(swiper) => {
                        swiperRef.current = swiper;
                    }}
                    spaceBetween={15}
                    navigation={false}
                    breakpoints={{
                        0: {
                            slidesPerView: 2,
                            spaceBetween: 10
                        },
                        600: {
                            slidesPerView: 3,
                            spaceBetween: 12
                        },
                        900: {
                            slidesPerView: 3,
                            spaceBetween: 15
                        },
                        1200: {
                            slidesPerView: 3,
                            spaceBetween: 20
                        }
                    }}
                    modules={[Grid, Navigation]}
                    style={{
                        width: '100%',
                        padding: '10px 0' // Remove side padding since we added it to the container
                    }}
                >
                    {giftCardTemplates.map((post) => (
                        <SwiperSlide
                            key={`gift-card-${post.id}`}
                            style={{
                                width: 'calc(100% / 3 - 10px)', // Distribute width evenly for 3 columns
                                height: 'auto'
                            }}
                        >
                            <GreetCard
                                post={post}
                                isSelected={selectedPost?.id === post.id}
                            />
                        </SwiperSlide>
                    ))}
                </Swiper>

                {giftCardTemplates?.length > 6 &&
                    (
                        <Box
                            className="swiper-button-prev-unique"
                            onClick={() => swiperRef.current?.slidePrev()}
                            sx={{
                                position: 'absolute',
                                left: '-5px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                zIndex: 1,
                                cursor: 'pointer',
                                fontSize: 18,
                                width: '28px',
                                height: '28px',
                                backgroundColor: 'primary.main',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                '&:hover': {
                                    backgroundColor: 'primary.dark'
                                },
                            }}
                        >
                            <ArrowBackIcon fontSize="inherit" color='white' />
                        </Box>
                    )
                }

                {giftCardTemplates?.length > 6 &&
                    (
                        <Box
                            className="swiper-button-next-unique"
                            onClick={() => swiperRef.current?.slideNext()}
                            sx={{
                                position: 'absolute',
                                right: '-5px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                zIndex: 1,
                                cursor: 'pointer',
                                fontSize: 18,
                                width: '28px',
                                height: '28px',
                                backgroundColor: 'primary.main',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                '&:hover': {
                                    backgroundColor: 'primary.dark'
                                },
                            }}
                        >
                            <ArrowForwardIcon fontSize="inherit" color='white' />
                        </Box>
                    )
                }
            </Box>
        )
    }

    return (
        <Box sx={{
            width: '100%',
            height: giftCardTemplates?.length > 3 ? 340 : 170,
            position: 'relative',
            px: 0,
            '& .swiper': {
                padding: '0',
                width: '100%',
            },
            '& .swiper-button-next, & .swiper-button-prev': {
                color: 'white.main',
            },
            '& .swiper-button-prev': {
                left: -25
            },
            '& .swiper-button-next': {
                right: -25
            }
        }}>
            {[...giftCardTemplates]?.length < 1 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Typography variant="body1">No gift card designs available</Typography>
                </Box>
            ) : (
                <>
                    <CardContainer />
                </>
            )}



        </Box>
    );
};

export default GreetCover;

